import { Toggle } from '@toggle/design-system';
import { KeyDevsObjectWithEntity } from '@toggle/toggle';
import { endOfWeek, startOfWeek } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useEarningsFilter } from '~/hooks/use-earnings-filter/useEarningsFilter';
import { useEconomicsEvents } from '~/hooks/use-economics-events/useEconomicsEvents';
import {
  KEY_DEV_TYPES_EXCEPT_EARNINGS,
  useKeyDevelopmentsEvents,
} from '~/hooks/use-key-developments-events/useKeyDevelopmentsEvents';
import { queryKeys } from '~/routes/app-paths';

import * as S from './CalendarView.styles';
import { EventOverview } from './event-overview/EventOverview';
import { EventTabs } from './event-tabs/EventTabs';
import { TabsType } from './utils';

export const CalendarView = () => {
  const { t } = useTranslation(['calendar']);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(TabsType.Earnings);
  const [activeDate, setActiveDate] = useState(
    new Date(new Date().setHours(0, 0, 0, 0))
  );

  useEffect(() => {
    const activeTabSearchParam = searchParams.get(
      queryKeys.activeCalendarTab
    ) as TabsType;
    const activeDateSearchParam = searchParams.get(
      queryKeys.activeCalendarDate
    );

    if (Object.values(TabsType).includes(activeTabSearchParam)) {
      setActiveTab(activeTabSearchParam);
    } else {
      setActiveTab(TabsType.Earnings);
    }

    if (activeDateSearchParam) {
      const parsedDate = new Date(parseFloat(activeDateSearchParam));
      setActiveDate(parsedDate);
    }
  }, [searchParams]);

  const monday = startOfWeek(activeDate, { weekStartsOn: 1 });
  const sunday = endOfWeek(activeDate, { weekStartsOn: 1 });

  const { data: economicData = {}, isLoading: isEconomicsLoading } =
    useEconomicsEvents({
      from: monday.toISOString(),
      to: sunday.toISOString(),
    });

  const { data: earningsData = {}, isLoading: isEarningsLoading } =
    useEarningsFilter({
      start_date: monday.toISOString(),
      end_date: sunday.toISOString(),
      watchlists: {
        all: true,
        identities: [],
      },
      sort_by: {
        field: 'reporting_date',
        desc: true,
      },
    });

  const { data: keyDevsData, isLoading: isKeyDevelopmentsLoading } =
    useKeyDevelopmentsEvents({
      entityTags: [],
      filters: [
        {
          start_date: monday.toISOString(),
          types: KEY_DEV_TYPES_EXCEPT_EARNINGS,
        },
      ],
      sorting: [
        {
          desc: true,
          id: 'effective_date',
        },
      ],
      initialPageParam: 30,
    });
  const companiesData =
    (keyDevsData?.pages.reduce((acc, page) => {
      const { grouped_key_devs: groupedKeyDevs } = page;
      return { ...acc, ...groupedKeyDevs };
    }, {}) as Record<string, KeyDevsObjectWithEntity[]>) ?? {};

  const data = {
    economics: { data: economicData, isLoading: isEconomicsLoading },
    earnings: { data: earningsData, isLoading: isEarningsLoading },
    companies: { data: companiesData, isLoading: isKeyDevelopmentsLoading },
  };

  return (
    <S.Container>
      <S.EventsWrapper>
        <S.Title>{t('calendar:pageTitle')}</S.Title>
        <EventOverview
          activeDate={activeDate}
          onDateChange={setActiveDate}
          startWeekDay={monday}
          endWeekDay={sunday}
          data={data}
        />
      </S.EventsWrapper>
      <div>
        <EventTabs activeTab={activeTab} />
        <S.TabsContentWrapper>
          <S.ToggleWrapper>
            <Toggle
              id="toggle-watchlist-only"
              leftLabel={t('calendar:table.toggleLabel')}
            />
          </S.ToggleWrapper>
          <div style={{ border: '1px solid grey', padding: '12px' }}>
            TABLE PLACEHOLDER
          </div>
        </S.TabsContentWrapper>
      </div>
    </S.Container>
  );
};
